import Image from 'next/image';
import styled, { css } from 'styled-components';

import StyledTypo from '@tmh-website/styles/typo';
import type { PrefixProperties } from '@tmh-website/shared/types';
import { Color } from '@tmh-website/shared/colors';
import { getScrollbarStylingCss } from '@tmh-website/ui/Scrollbar';

import AudienceTabs from '../AudienceTabs/AudienceTabs';

export const Menu = styled.div<{ $isOpened: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 300;
  width: 100%;
  max-width: none;
  height: 100vh;
  height: 100dvh;
  max-height: none;
  padding: 0;
  margin: 0;
  opacity: 1;
  transition: opacity ${props => props.theme.transitionTiming};
  @media (${props => props.theme.breakpoint.md.up}) {
    display: flex;
  }

  ${props =>
    props.$isOpened === false &&
    css`
      opacity: 0 !important;
      pointer-events: none !important;
    `};
`;

export const GlobalCol = styled.div`
  z-index: 302;
  display: flex;
  flex: auto 1 0;
  flex-direction: column;
  height: 100%;
  overflow: auto;
  background-color: ${props => props.theme.color[Color.Blue_1000_Deep]};
  @media (${props => props.theme.breakpoint.md.up}) {
    max-width: calc(50% - 40px);
    min-height: 100vh;
  }
  @media (${props => props.theme.breakpoint.lg.up}) {
    max-width: 440px;
  }
`;

export const Toolbar = styled.div`
  z-index: 2;
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  min-height: ${props => props.theme.headerNavigationHeightMobile};
  padding: 10px ${props => props.theme.wrapperPaddingMobile};
  color: ${props => props.theme.color[Color.Neutral_0_White]};
  background-color: ${props => props.theme.color[Color.Blue_1000_Deep]};
  @media (${props => props.theme.breakpoint.md.up}) {
    position: fixed;
    top: 0;
    right: 0;
    gap: 4px;
    max-width: 50vw;
    padding: 16px ${props => props.theme.wrapperPadding};
    background-color: transparent;
  }
  @media (${props => props.theme.breakpoint.lg.up}) {
    right: 50%;
    gap: 10px;
    align-items: center;
    max-width: ${props => props.theme.siteMaxWidthLarge};
    min-height: ${props => props.theme.headerNavigationHeight};
    padding: 10px ${props => props.theme.wrapperPadding};
    transform: translateX(50%);
  }
`;

export const ToolbarButtonsContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.gap.xSmall};
  align-items: center;
`;

export const AdjustedAudienceTabs = styled(AudienceTabs)`
  @media (${props => props.theme.breakpoint.lg.up}) {
    display: none !important;
  }
`;

const navContainerCss = css`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.gap.xSmall};
  align-items: flex-start;
  padding: 20px ${props => props.theme.wrapperPaddingMobile};
  overflow: visible;
  @media (${props => props.theme.breakpoint.md.up}) {
    gap: ${props => props.theme.gap.xSmall};
    padding: 80px ${props => props.theme.gap.medium};
  }
  @media (${props => props.theme.breakpoint.lg.up}) {
    padding: 80px ${props => props.theme.gap.large};
  }
`;

export const GlobalNav = styled.div`
  ${navContainerCss}
  flex-grow: 1;
  background-color: ${props => props.theme.color[Color.Blue_1000_Deep]};
  @media (${props => props.theme.breakpoint.md.down}) {
    align-items: stretch;
    padding-right: 0;
    padding-left: 0;
  }
  @media (${props => props.theme.breakpoint.md.up}) {
    gap: 0;
    padding-bottom: 30px;
    overflow-y: auto;
    ${getScrollbarStylingCss(Color.Blue_1000_Deep)};
  }
`;

export const CategoryCol = styled.nav<{ $isVisible: boolean }>`
  ${navContainerCss};
  background-color: ${props => props.theme.color[Color.Blue_900]};
  transition: transform 0.5s ease;
  @media (${props => props.theme.breakpoint.md.up}) {
    position: absolute;
    top: 0;
    left: calc(50% - 40px);
    z-index: 1;
    width: calc(50% - 40px);
    height: 100vh;
    padding-right: 60px;
    padding-left: 60px;
    overflow-y: auto;
    transform: translateX(${props => (props.$isVisible ? '0' : '-101%')});
    ${props => props.$isVisible === false && `pointer-events: none;`}
    ${getScrollbarStylingCss(Color.Blue_900)};
  }
  @media (${props => props.theme.breakpoint.lg.up}) {
    left: 440px;
    width: auto;
    min-width: 400px;
  }
`;

export const BackgroundPlane = styled.div`
  display: none;
  @media (${props => props.theme.breakpoint.md.up}) {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    display: block;
    width: 100%;
    max-width: calc(50% - 40px);
    height: 100%;
    pointer-events: none;
    background-color: ${props => props.theme.color[Color.Blue_1000_Deep]};
  }
  @media (${props => props.theme.breakpoint.lg.up}) {
    max-width: 440px;
  }
`;

export const QuickLinksContainer = styled.nav`
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  padding-top: 30px;
  margin-top: 20px;
  @media (${props => props.theme.breakpoint.md.up}) {
    padding-top: 40px;
    margin-top: ${props => props.theme.gap.medium};
  }

  &::before {
    position: absolute;
    top: 0;
    left: ${props => props.theme.wrapperPaddingMobile};
    display: block;
    width: calc(100% - ${props => `${props.theme.wrapperPaddingMobile} - ${props.theme.wrapperPaddingMobile}`});
    height: 1px;
    content: '';
    background-color: ${props => props.theme.color[Color.Neutral_0_White]};
    opacity: 0.3;
    @media (${props => props.theme.breakpoint.md.up}) {
      left: 0;
      width: 100%;
    }
  }
`;

export const QuickLink = styled.a`
  ${StyledTypo.uiTextCss};
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 6px ${props => props.theme.wrapperPaddingMobile};
  margin: 0 0 4px;
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.color[Color.Neutral_0_White]};
  opacity: 0.6;
  transition: opacity ${props => props.theme.transitionTiming};
  @media (${props => props.theme.breakpoint.md.up}) {
    padding: 6px 10px;
    margin: 0 -10px 4px;
  }

  &:hover {
    text-decoration: none;
    opacity: 1;
  }
`;

export const BottomContainer = styled.div`
  ${navContainerCss}
  z-index: 3;
  gap: 8px;
  padding-top: 20px;
  background-color: ${props => props.theme.color[Color.Blue_900]};
  @media (${props => props.theme.breakpoint.md.up}) {
    padding-block: 30px;
    background-color: ${props => props.theme.color[Color.Blue_1000_Deep]};
  }
`;

export const BottomButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.gap.xSmall};
  width: 100%;
  color: ${props => props.theme.color[Color.Neutral_0_White]};
  @media (${props => props.theme.breakpoint.sm.up}) {
    flex-flow: wrap row;
    justify-content: center;
  }
  @media (${props => props.theme.breakpoint.md.up}) {
    justify-content: flex-start;
  }
`;

export type LogoWrapperProps = PrefixProperties<
  {
    maxWidth?: string;
  },
  '$'
>;

export const LogoWrapper = styled.div<LogoWrapperProps>`
  margin: 10px auto 0;
  color: ${props => props.theme.color[Color.Neutral_0_White]};
  @media (${props => props.theme.breakpoint.md.up}) {
    position: fixed;
    right: ${props => props.theme.gap.medium};
    bottom: ${props => props.theme.gap.medium};
    z-index: 2;
    margin: 0;
  }

  svg {
    width: 160px;
    max-width: ${({ $maxWidth }) => $maxWidth};
    @media (${props => props.theme.breakpoint.sm.up}) {
      width: 234px;
    }
  }
`;

export const ImageCol = styled.div`
  position: relative;
  flex-grow: 1;
  background-color: ${props => props.theme.color[Color.Blue_800]};
  @media (${props => props.theme.breakpoint.md.down}) {
    display: none;
  }

  &::after {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 100%;
    content: '';
    background-color: ${props => props.theme.color[Color.Blue_800]};
    opacity: 0.8;
  }
`;

export const MenuImage = styled(Image)<{ $isVisible: boolean }>`
  position: relative;
  pointer-events: none;
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  transition: opacity 0.5s ease;
`;
